import { useState, useEffect } from "react";
import { RefreshCw, Plus, Trash2, Play, Pause, Download, Eye, X, Upload } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDateTime } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import type { Campaign, List, MessageBlock, CampaignJob } from "../types";

export default function CampaignsPage() {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [lists, setLists] = useState<List[]>([]);

  // Composer state
  const [cName, setCName] = useState("");
  const [cListId, setCListId] = useState<number | null>(null);
  const [cDryRun, setCDryRun] = useState(true);
  const [cBlocks, setCBlocks] = useState<MessageBlock[]>([]);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState("");
  const [previewOutput, setPreviewOutput] = useState("");

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);
  const [jobs, setJobs] = useState<CampaignJob[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const jobsPageSize = 25;

  const fetchCampaigns = async () => {
    try {
      const res = await api.get("/campaigns");
      if (res.data.ok) setCampaigns(res.data.campaigns || []);
    } catch {}
  };

  const fetchLists = async () => {
    try {
      const res = await api.get("/lists");
      if (res.data.ok) setLists(res.data.lists || []);
    } catch {}
  };

  useEffect(() => { fetchCampaigns(); fetchLists(); }, []);

  // Composer
  const addBlock = (type: "text" | "image") => {
    setCBlocks([...cBlocks, { type, content: "" }]);
  };

  const updateBlock = (i: number, content: string) => {
    const b = [...cBlocks];
    b[i] = { ...b[i], content };
    setCBlocks(b);
  };

  const uploadImage = async (i: number, file: File) => {
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/campaigns/upload-image", fd);
      if (res.data.ok && res.data.url) {
        updateBlock(i, res.data.url);
        toast("Image uploaded", "success");
      } else toast(res.data.error || "Upload failed", "error");
    } catch (err: any) {
      toast(err.response?.data?.error || "Upload failed", "error");
    } finally {
      setUploadingIdx(null);
    }
  };

  const removeBlock = (i: number) => {
    setCBlocks(cBlocks.filter((_, idx) => idx !== i));
  };

  const createCampaign = async () => {
    if (!cName.trim()) { toast("Name required", "error"); return; }
    if (!cListId) { toast("Select a list", "error"); return; }
    if (cBlocks.length === 0) { toast("Add at least one message block", "error"); return; }

    try {
      const res = await api.post("/campaigns", {
        name: cName.trim(),
        list_id: cListId,
        dry_run: cDryRun ? 1 : 0,
        message_json: { messages: cBlocks },
      });
      if (res.data.ok) {
        toast("Campaign created", "success");
        setCName(""); setCListId(null); setCDryRun(true); setCBlocks([]);
        fetchCampaigns();
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const preview = async () => {
    if (!previewId.trim()) return;
    // Need a campaign id — use most recently created
    const lastDraft = campaigns.find((c) => c.status === "draft");
    if (!lastDraft) { toast("Create a draft campaign first", "error"); return; }
    try {
      const res = await api.post("/campaigns/" + lastDraft.id + "/preview?recipient_id=" + previewId.trim());
      if (res.data.ok) {
        setPreviewOutput(JSON.stringify(res.data.rendered, null, 2));
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  // Detail modal
  const openDetail = async (campaign: Campaign) => {
    try {
      const res = await api.get("/campaigns/" + campaign.id);
      if (res.data.ok) {
        setActiveCampaign(res.data.campaign);
        setJobsPage(1);
        setDetailModal(true);
        fetchJobs(campaign.id, 1);
      }
    } catch {}
  };

  const fetchJobs = async (campaignId: number, page: number) => {
    try {
      const res = await api.get("/campaigns/" + campaignId + "/jobs", { params: { page, page_size: jobsPageSize } });
      if (res.data.ok) {
        setJobs(res.data.jobs || []);
        setJobsTotal(res.data.total || 0);
      }
    } catch {}
  };

  const refreshDetail = async () => {
    if (!activeCampaign) return;
    try {
      const res = await api.get("/campaigns/" + activeCampaign.id);
      if (res.data.ok) setActiveCampaign(res.data.campaign);
    } catch {}
    fetchJobs(activeCampaign.id, jobsPage);
  };

  useEffect(() => {
    if (activeCampaign) fetchJobs(activeCampaign.id, jobsPage);
  }, [jobsPage]);

  // Auto-refresh while detail modal is open and campaign is running
  useEffect(() => {
    if (!detailModal || !activeCampaign || activeCampaign.status !== "running") return;
    const interval = setInterval(refreshDetail, 5000);
    return () => clearInterval(interval);
  }, [detailModal, activeCampaign?.status]);

  const startCampaign = async () => {
    if (!activeCampaign) return;
    try {
      const res = await api.post("/campaigns/" + activeCampaign.id + "/start");
      if (res.data.ok) { toast("Campaign started", "success"); refreshDetail(); }
      else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const pauseCampaign = async () => {
    if (!activeCampaign) return;
    try {
      const res = await api.post("/campaigns/" + activeCampaign.id + "/pause");
      if (res.data.ok) { toast("Campaign paused", "success"); refreshDetail(); }
      else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const resumeCampaign = async () => {
    if (!activeCampaign) return;
    try {
      const res = await api.post("/campaigns/" + activeCampaign.id + "/resume");
      if (res.data.ok) { toast("Campaign resumed", "success"); refreshDetail(); }
      else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const jobsTotalPages = Math.max(1, Math.ceil(jobsTotal / jobsPageSize));
  const stats = activeCampaign?.stats;
  const completed = (stats?.sent || 0) + (stats?.failed || 0) + (stats?.skipped || 0);
  const progressPct = stats?.total ? Math.round((completed / stats.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Campaigns</h1>
        <p className="text-zinc-400 text-sm mt-1">Create and manage WhatsApp campaigns</p>
      </div>

      {/* Composer */}
      <Card title="Campaign Composer" badge={<StatusBadge status="draft" />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Campaign name</label>
            <input value={cName} onChange={(e) => setCName(e.target.value)} placeholder="e.g. March promo"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">List</label>
            <select value={cListId || ""} onChange={(e) => setCListId(parseInt(e.target.value) || null)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500 transition-colors">
              <option value="">-- Select --</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cDryRun} onChange={(e) => setCDryRun(e.target.checked)}
                className="rounded border-zinc-600 bg-zinc-900 text-fuchsia-500 focus:ring-fuchsia-500" />
              <span className="text-sm text-zinc-300">Dry run</span>
            </label>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={() => addBlock("text")} className="px-3 py-2 text-sm rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
              + Text
            </button>
            <button onClick={() => addBlock("image")} className="px-3 py-2 text-sm rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
              + Image
            </button>
          </div>
        </div>

        <p className="text-xs text-zinc-500 mb-3">Tokens: {'{name}'}, {'{last_name}'}, {'{appointments}'} (payload override)</p>

        {/* Blocks */}
        <div className="space-y-3 mb-4">
          {cBlocks.map((b, i) => (
            <div key={i} className="flex gap-3 items-start">
              <span className="text-xs text-zinc-500 mt-3 w-12 shrink-0">{b.type === "text" ? "Text" : "Image"}</span>
              <div className="flex-1">
                <textarea
                  value={b.content}
                  onChange={(e) => updateBlock(i, e.target.value)}
                  placeholder={b.type === "text" ? "Message text..." : "Image URL or upload below..."}
                  rows={b.type === "text" ? 3 : 1}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors resize-none text-sm"
                />
                {b.type === "image" && (
                  <div className="flex items-center gap-3 mt-2">
                    <label className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors cursor-pointer inline-flex items-center gap-1.5">
                      <Upload size={13} />
                      {uploadingIdx === i ? "Uploading..." : "Upload image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        disabled={uploadingIdx === i}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadImage(i, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {b.content && (
                      <img src={b.content} alt="preview" className="h-12 w-12 object-cover rounded-lg border border-zinc-800" />
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => removeBlock(i)} className="text-zinc-500 hover:text-red-400 mt-3 transition-colors">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>

        <button onClick={createCampaign} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-600 to-orange-500 text-white font-semibold hover:from-fuchsia-500 hover:to-orange-400 transition-all">
          Save Draft
        </button>

        {/* Preview */}
        <div className="mt-6 pt-4 border-t border-zinc-800">
          <div className="flex gap-3 items-end mb-3">
            <div className="flex-1 max-w-xs">
              <label className="text-sm text-zinc-400 mb-1 block">Preview recipient ID</label>
              <input value={previewId} onChange={(e) => setPreviewId(e.target.value)} placeholder="e.g. 123"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
            </div>
            <button onClick={preview} className="px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors flex items-center gap-2">
              <Eye size={16} /> Preview
            </button>
          </div>
          {previewOutput && (
            <pre className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-300 overflow-x-auto">{previewOutput}</pre>
          )}
        </div>
      </Card>

      {/* Campaigns List */}
      <Card title="Campaigns" badge={<span className="text-xs text-zinc-500">{campaigns.length}</span>} actions={
        <button onClick={fetchCampaigns} className="text-zinc-400 hover:text-white transition-colors"><RefreshCw size={16} /></button>
      }>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-zinc-800">
                <th className="pb-3 font-medium">ID</th>
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Progress</th>
                <th className="pb-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-zinc-600 py-8">No campaigns</td></tr>
              ) : campaigns.map((c) => {
                const s = c.stats;
                const done = s ? s.sent + s.failed + s.skipped : 0;
                const pct = s && s.total ? Math.round((done / s.total) * 100) : 0;
                return (
                  <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-2.5 text-zinc-400">{c.id}</td>
                    <td className="py-2.5 font-medium text-white">{c.name}</td>
                    <td className="py-2.5"><StatusBadge status={c.status} /></td>
                    <td className="py-2.5">
                      {s && s.total > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden max-w-[120px]">
                            <div className="h-full bg-gradient-to-r from-fuchsia-500 to-orange-500 rounded-full transition-all" style={{ width: pct + "%" }} />
                          </div>
                          <span className="text-xs text-zinc-400">{pct}%</span>
                        </div>
                      ) : <span className="text-xs text-zinc-600">—</span>}
                    </td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => openDetail(c)} className="text-xs px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Campaign Detail Modal */}
      <Modal open={detailModal} onClose={() => { setDetailModal(false); fetchCampaigns(); }} title={"Campaign #" + (activeCampaign?.id || "") + ": " + (activeCampaign?.name || "")} wide>
        {activeCampaign && (
          <div>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <StatusBadge status={activeCampaign.status} size="md" />
              <span className="text-xs text-zinc-500">Mode: {activeCampaign.dry_run ? "DRY RUN" : "LIVE"}</span>
              <span className="text-xs text-zinc-500">Created: {fmtDateTime(activeCampaign.created_at)}</span>
            </div>

            {/* Progress bar */}
            {stats && stats.total > 0 && (
              <div className="mb-4">
                <div className="h-4 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-fuchsia-500 to-orange-500 rounded-full transition-all" style={{ width: progressPct + "%" }} />
                </div>
                <div className="flex justify-between mt-1 text-xs text-zinc-400">
                  <span>{completed} / {stats.total} completed ({progressPct}%)</span>
                  <span>
                    <span className="text-emerald-400">{stats.sent}</span> sent{" "}
                    <span className="text-red-400">{stats.failed}</span> failed{" "}
                    <span className="text-amber-400">{stats.skipped}</span> skipped
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mb-6">
              {activeCampaign.status === "draft" && (
                <button onClick={startCampaign} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 text-sm transition-colors">
                  <Play size={14} /> Start
                </button>
              )}
              {activeCampaign.status === "running" && (
                <button onClick={pauseCampaign} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 text-white hover:bg-amber-500 text-sm transition-colors">
                  <Pause size={14} /> Pause
                </button>
              )}
              {activeCampaign.status === "paused" && (
                <button onClick={resumeCampaign} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 text-sm transition-colors">
                  <Play size={14} /> Resume
                </button>
              )}
              <a href={"/api/campaigns/" + activeCampaign.id + "/results.json"} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-sm transition-colors">
                <Download size={14} /> Export JSON
              </a>
              <button onClick={refreshDetail} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-sm transition-colors">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>

            {/* Jobs */}
            <h4 className="text-sm font-semibold text-white mb-3">Jobs</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-left border-b border-zinc-800">
                    <th className="pb-3 font-medium">Phone</th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Attempts</th>
                    <th className="pb-3 font-medium">Last Error</th>
                    <th className="pb-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-zinc-600 py-8">No jobs</td></tr>
                  ) : jobs.map((j) => (
                    <tr key={j.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="py-2.5 font-mono text-xs">{j.phone}</td>
                      <td className="py-2.5">{[j.name, j.last_name].filter(Boolean).join(" ") || "—"}</td>
                      <td className="py-2.5"><StatusBadge status={j.status} /></td>
                      <td className="py-2.5 text-center">{j.attempts}</td>
                      <td className="py-2.5 text-xs text-red-400 max-w-[200px] truncate">{j.last_error || ""}</td>
                      <td className="py-2.5 text-xs text-zinc-500">{fmtDateTime(j.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={jobsPage} totalPages={jobsTotalPages} total={jobsTotal}
              onPrev={() => setJobsPage((p) => Math.max(1, p - 1))} onNext={() => setJobsPage((p) => p + 1)} />
          </div>
        )}
      </Modal>
    </div>
  );
}
